import React, { useState, useEffect, useCallback } from 'react';
import { Wrench, Plus, Edit2, Trash2, X, Euro, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'entretien_courant', label: 'Entretien courant', color: 'bg-blue-100 text-blue-700' },
  { value: 'reparation', label: 'Réparation', color: 'bg-orange-100 text-orange-700' },
  { value: 'travaux_amelioration', label: 'Travaux d\'amélioration', color: 'bg-green-100 text-green-700' },
  { value: 'mise_conformite', label: 'Mise en conformité', color: 'bg-purple-100 text-purple-700' },
  { value: 'urgence', label: 'Urgence', color: 'bg-red-100 text-red-700' },
];

export default function TravauxTab({ buildingId, token, onUpdate }) {
  const [travaux, setTravaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  const fetchTravaux = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/carnet-entretien/travaux/${buildingId}?annee=${filterYear}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setTravaux(await response.json());
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  }, [buildingId, token, filterYear]);

  useEffect(() => { fetchTravaux(); }, [fetchTravaux]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingId ? `${API_URL}/api/carnet-entretien/travaux/${editingId}` : `${API_URL}/api/carnet-entretien/travaux`;
    const method = editingId ? 'PUT' : 'POST';
    try {
      const response = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, building_id: buildingId })
      });
      if (response.ok) {
        toast.success(editingId ? 'Travaux modifiés' : 'Travaux enregistrés');
        setShowModal(false); setEditingId(null); setForm({});
        fetchTravaux();
        if (onUpdate) onUpdate();
      }
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    try {
      await fetch(`${API_URL}/api/carnet-entretien/travaux/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Supprimé');
      fetchTravaux();
      if (onUpdate) onUpdate();
    } catch (error) { toast.error('Erreur'); }
  };

  const openNew = () => { setForm({ categorie: 'entretien_courant', date_realisation: new Date().toISOString().split('T')[0] }); setEditingId(null); setShowModal(true); };
  const openEdit = (t) => { setForm(t); setEditingId(t.id); setShowModal(true); };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const formatMoney = (a) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(a || 0);

  const totalAnnee = travaux.reduce((sum, t) => sum + (t.montant_ttc || 0), 0);
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Historique des travaux</h2>
          <p className="text-sm text-gray-500">{travaux.length} intervention(s) - Total: {formatMoney(totalAnnee)}</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filterYear} onChange={(e) => setFilterYear(parseInt(e.target.value))} className="px-3 py-2 border border-gray-200 rounded-lg">
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]">
            <Plus className="h-5 w-5" />Ajouter
          </button>
        </div>
      </div>

      {travaux.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Wrench className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Aucun travaux enregistré pour {filterYear}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intervention</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prestataire</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant TTC</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {travaux.map(t => {
                const cat = categories.find(c => c.value === t.categorie) || categories[0];
                return (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{formatDate(t.date_realisation)}</td>
                    <td className="px-4 py-3"><p className="font-medium text-gray-900">{t.titre}</p>{t.description && <p className="text-xs text-gray-500 truncate max-w-xs">{t.description}</p>}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-medium ${cat.color}`}>{cat.label}</span></td>
                    <td className="px-4 py-3 text-sm text-gray-600">{t.prestataire || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">{formatMoney(t.montant_ttc)}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => openEdit(t)} className="p-1 hover:bg-gray-100 rounded"><Edit2 className="h-4 w-4 text-gray-400" /></button>
                      <button onClick={() => handleDelete(t.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4 text-red-400" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-semibold">{editingId ? 'Modifier' : 'Enregistrer'} des travaux</h3>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div><label className="block text-sm font-medium mb-1">Titre *</label><input type="text" value={form.titre || ''} onChange={(e) => setForm({...form, titre: e.target.value})} required className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Remplacement chaudière" /></div>
              <div><label className="block text-sm font-medium mb-1">Description</label><textarea value={form.description || ''} onChange={(e) => setForm({...form, description: e.target.value})} rows="2" className="w-full px-3 py-2 border rounded-lg" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Catégorie</label>
                  <select value={form.categorie || ''} onChange={(e) => setForm({...form, categorie: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div><label className="block text-sm font-medium mb-1">Date réalisation *</label><input type="date" value={form.date_realisation || ''} onChange={(e) => setForm({...form, date_realisation: e.target.value})} required className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Prestataire</label><input type="text" value={form.prestataire || ''} onChange={(e) => setForm({...form, prestataire: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Montant HT (€)</label><input type="number" step="0.01" value={form.montant || ''} onChange={(e) => setForm({...form, montant: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-1">Montant TTC (€)</label><input type="number" step="0.01" value={form.montant_ttc || ''} onChange={(e) => setForm({...form, montant_ttc: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Localisation</label><input type="text" value={form.localisation || ''} onChange={(e) => setForm({...form, localisation: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Chaufferie, Toiture" /></div>
              
              {/* Décision AG */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-900 mb-3">Décision d'AG (si applicable)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.vote_ag_requis || false} onChange={(e) => setForm({...form, vote_ag_requis: e.target.checked})} className="w-4 h-4 text-[#00D3A8] rounded" />
                      <span className="text-sm">Travaux votés en AG</span>
                    </label>
                  </div>
                  {form.vote_ag_requis && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Date de l'AG</label>
                      <input type="date" value={form.vote_ag_date || ''} onChange={(e) => setForm({...form, vote_ag_date: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                  )}
                </div>
                {form.vote_ag_requis && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-1">Référence résolution</label>
                    <input type="text" value={form.resolution_reference || ''} onChange={(e) => setForm({...form, resolution_reference: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Résolution n°5 - AG du 15/03/2025" />
                  </div>
                )}
              </div>

              {/* Garantie */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-900 mb-3">Garantie</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Durée garantie (années)</label>
                    <input type="number" value={form.garantie_duree || ''} onChange={(e) => setForm({...form, garantie_duree: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date fin garantie</label>
                    <input type="date" value={form.garantie_fin || ''} onChange={(e) => setForm({...form, garantie_fin: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]">{editingId ? 'Modifier' : 'Enregistrer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
